import { TFunction } from 'i18next';
import { ColorEnum } from 'graphql-common';
import { Option } from '@lib/interfaces/form';
import { ChipTypes } from '@lib/components/Chip/Chip';

/**
 * Maps the backend `ColorEnum` (e.g. `BLUE`) to the frontend `ChipTypes`
 * rendering vocabulary (e.g. `chip-blue`, which doubles as the SCSS class name).
 *
 * Note the `CORAL` spelling: the enum is `CORAL` while the chip class is the
 * double-R `chip-corral`.
 */
export const COLOR_ENUM_TO_CHIP_TYPE: Record<ColorEnum, ChipTypes> = {
  [ColorEnum.Aquamarine]: ChipTypes.chipAquamarine,
  [ColorEnum.Black]: ChipTypes.chipBlack,
  [ColorEnum.Blue]: ChipTypes.chipBlue,
  [ColorEnum.Brown]: ChipTypes.chipBrown,
  [ColorEnum.Coral]: ChipTypes.chipCorral,
  [ColorEnum.Gray]: ChipTypes.chipGray,
  [ColorEnum.Green]: ChipTypes.chipGreen,
  [ColorEnum.Lime]: ChipTypes.chipLime,
  [ColorEnum.Orange]: ChipTypes.chipOrange,
  [ColorEnum.Pink]: ChipTypes.chipPink,
  [ColorEnum.Red]: ChipTypes.chipRed,
  [ColorEnum.Violet]: ChipTypes.chipViolet,
  [ColorEnum.White]: ChipTypes.chipWhite,
  [ColorEnum.Yellow]: ChipTypes.chipYellow,
};

const CHIP_TYPE_VALUES = new Set<string>(Object.values(ChipTypes));

/**
 * Normalizes a color value into a `ChipTypes` for rendering. Accepts a
 * `ColorEnum` (e.g. `BLUE`), a `chip-*` class, or a bare color name (e.g.
 * `blue`, or the double-R `corral`). Falls back to `fallback` (default
 * `chipGray`) for empty / unknown values.
 */
export function colorToChipType(
  value?: string | null,
  fallback: ChipTypes = ChipTypes.chipGray,
): ChipTypes {
  if (!value) return fallback;
  if (value.startsWith('chip-')) return value as ChipTypes;
  const fromEnum = COLOR_ENUM_TO_CHIP_TYPE[value.toUpperCase() as ColorEnum];
  if (fromEnum) return fromEnum;
  // Bare names that map straight onto a chip class (e.g. `corral`, whose enum is
  // the single-R `CORAL`).
  const asChipType = `chip-${value.toLowerCase()}`;
  return CHIP_TYPE_VALUES.has(asChipType)
    ? (asChipType as ChipTypes)
    : fallback;
}

/**
 * Builds the selectable color options for forms whose color list is owned by the
 * backend (`ColorEnum`). The option `value` is the enum (what the backend
 * stores), while `color` carries the `ChipTypes` used to render the swatch.
 */
export default function getColorOptions({
  t,
}: {
  t: TFunction<'translation', undefined>;
}): Option[] {
  return (Object.values(ColorEnum) as ColorEnum[]).map((color) => {
    const chipType = COLOR_ENUM_TO_CHIP_TYPE[color];
    return {
      value: color,
      label: t(chipType.replace('chip-', '')),
      color: chipType,
    };
  }) as Option[];
}
